import { z } from "zod";

export const MARKETING_ATTRIBUTION_STORAGE_KEY = "kitsulab-marketing-attribution";
export const MARKETING_ATTRIBUTION_MAX_AGE_MS = 90 * 24 * 60 * 60 * 1000;
export const MARKETING_ATTRIBUTION_MAX_FUTURE_MS = 5 * 60 * 1000;

const MARKETING_ATTRIBUTION_VALUE_MAX_LENGTH = 100;
const MARKETING_LANDING_PAGE_MAX_LENGTH = 512;

export const MARKETING_ATTRIBUTION_FIELDS = [
	"source",
	"medium",
	"campaign",
	"content",
	"term",
] as const;

export type MarketingAttributionField = (typeof MARKETING_ATTRIBUTION_FIELDS)[number];

export type StorageLike = {
	getItem(key: string): string | null;
	setItem(key: string, value: string): void;
	removeItem(key: string): void;
};

export function normalizeMarketingAttributionValue(value: string): string {
	return value
		.normalize("NFKC")
		.trim()
		.toLowerCase()
		.replace(/\s+/g, "-")
		.replace(/[^a-z0-9._~-]+/g, "-")
		.replace(/-+/g, "-")
		.replace(/^[-.]+|[-.]+$/g, "")
		.slice(0, MARKETING_ATTRIBUTION_VALUE_MAX_LENGTH);
}

const OptionalMarketingAttributionValueSchema = z.preprocess(
	(value) => {
		if (typeof value !== "string") return value;
		const normalized = normalizeMarketingAttributionValue(value);
		return normalized.length > 0 ? normalized : undefined;
	},
	z
		.string()
		.min(1)
		.max(MARKETING_ATTRIBUTION_VALUE_MAX_LENGTH)
		.regex(/^[a-z0-9][a-z0-9._~-]*$/)
		.optional(),
);

export const MarketingAttributionSchema = z
	.object({
		source: OptionalMarketingAttributionValueSchema,
		medium: OptionalMarketingAttributionValueSchema,
		campaign: OptionalMarketingAttributionValueSchema,
		content: OptionalMarketingAttributionValueSchema,
		term: OptionalMarketingAttributionValueSchema,
		landingPage: z
			.string()
			.min(1)
			.max(MARKETING_LANDING_PAGE_MAX_LENGTH)
			.regex(/^\/[A-Za-z0-9._~!$&'()*+,;=:@/%-]*$/),
		firstSeenAt: z.string().datetime({ offset: true }),
	})
	.strict();

export type MarketingAttribution = z.infer<typeof MarketingAttributionSchema>;

export const ProductActivationEventTypeSchema = z.enum([
	"account_created",
	"roblox_link_completed",
	"project_created",
	"first_content",
	"pairing_started",
	"plugin_connected",
	"first_sync",
	"runtime_detected",
	"onboarding_step_displayed",
	"first_item_cta_clicked",
	"studio_cta_clicked",
	"sync_instruction_displayed",
	"play_instruction_displayed",
	"onboarding_completed",
]);

export type ProductActivationEventType = z.infer<
	typeof ProductActivationEventTypeSchema
>;

function getUtmParameter(field: MarketingAttributionField): string {
	return `utm_${field}`;
}

function parseStoredAttribution(raw: string): MarketingAttribution | null {
	try {
		const parsed: unknown = JSON.parse(raw);
		const result = MarketingAttributionSchema.safeParse(parsed);
		return result.success ? result.data : null;
	} catch {
		return null;
	}
}

export function hasMarketingAttribution(
	attribution: MarketingAttribution,
): boolean {
	return MARKETING_ATTRIBUTION_FIELDS.some(
		(field) => attribution[field] !== undefined,
	);
}

export function isMarketingAttributionCurrent(
	attribution: MarketingAttribution,
	now = new Date(),
): boolean {
	const firstSeenAt = Date.parse(attribution.firstSeenAt);
	if (!Number.isFinite(firstSeenAt)) return false;
	const nowAt = now.getTime();
	return (
		firstSeenAt >= nowAt - MARKETING_ATTRIBUTION_MAX_AGE_MS &&
		firstSeenAt <= nowAt + MARKETING_ATTRIBUTION_MAX_FUTURE_MS
	);
}

export function readMarketingAttribution(
	storage: StorageLike,
	now = new Date(),
): MarketingAttribution | null {
	try {
		const raw = storage.getItem(MARKETING_ATTRIBUTION_STORAGE_KEY);
		if (!raw) return null;
		const attribution = parseStoredAttribution(raw);
		if (
			!attribution ||
			!hasMarketingAttribution(attribution) ||
			!isMarketingAttributionCurrent(attribution, now)
		) {
			storage.removeItem(MARKETING_ATTRIBUTION_STORAGE_KEY);
			return null;
		}
		return attribution;
	} catch {
		return null;
	}
}

export function clearMarketingAttribution(storage: StorageLike): void {
	try {
		storage.removeItem(MARKETING_ATTRIBUTION_STORAGE_KEY);
	} catch {
		return;
	}
}

export function captureMarketingAttribution(
	url: URL,
	storage: StorageLike,
	now = new Date(),
): MarketingAttribution | null {
	const existing = readMarketingAttribution(storage, now);
	if (existing) return existing;

	const result = MarketingAttributionSchema.safeParse({
		source: url.searchParams.get(getUtmParameter("source")) ?? undefined,
		medium: url.searchParams.get(getUtmParameter("medium")) ?? undefined,
		campaign: url.searchParams.get(getUtmParameter("campaign")) ?? undefined,
		content: url.searchParams.get(getUtmParameter("content")) ?? undefined,
		term: url.searchParams.get(getUtmParameter("term")) ?? undefined,
		landingPage: url.pathname,
		firstSeenAt: now.toISOString(),
	});
	if (!result.success || !hasMarketingAttribution(result.data)) return null;

	try {
		storage.setItem(
			MARKETING_ATTRIBUTION_STORAGE_KEY,
			JSON.stringify(result.data),
		);
		return result.data;
	} catch {
		return null;
	}
}

export function urlWithoutMarketingParameters(url: URL): string {
	const cleaned = new URL(url.toString());
	for (const field of MARKETING_ATTRIBUTION_FIELDS) {
		cleaned.searchParams.delete(getUtmParameter(field));
	}
	return `${cleaned.pathname}${cleaned.search}${cleaned.hash}`;
}
